import {Apollo, gql} from 'apollo-angular';
import {InMemoryCache} from '@apollo/client/cache';
import {Observable, Subject, pipe} from 'rxjs';
import {map} from 'rxjs/internal/operators/map';
import {FetchResult} from '@apollo/client/link/core/types';
import {CoreService} from 'src/app/core/service/core.service';
import {Injectable} from '@angular/core';
import {BaseService} from 'src/app/kanso-common/core/service';
import {HttpClient} from '@angular/common/http';
import {
  CreateProjectCommand,
  CreateProjectFeeTableCommand,
  CreateProjectOverrideCommand,
  DeleteProjectFeeTableCommand,
  DeleteProjectOverrideCommand,
  ICreateProjectAttributeDataResponse,
  ICreateProjectDataResponse,
  ICreateProjectOverrideDataResponse,
  IDeleteProjectOverrideDataResponse,
  IProjectAttributeCommand,
  IUpdateProjectAttributeDataResponse,
  IUpdateProjectDataResponse,
  IUpdateProjectOverrideDataResponse,
  ProjectFeeStatus,
  ProjectFeeTable,
  ProjectModel,
  ProjectOverride,
  UpdateProjectCommand,
  UpdateProjectFeeTableCommand,
  UpdateProjectCustomAttributes,
  UpdateProjectOverrideCommand,
  CreateTaxCreditSetAsidesCommand,
  UpdateTaxCreditSetAsidesCommand,
  TaxCreditSetAsides,
  IUpdateTaxCreditSetAsidesDataResponse,
  ICreateTaxCreditSetAsidesDataResponse,
} from './project.model';
import {ComplexSaveResult, CustomAttributesValue, ICustomAttributesValue} from 'src/app/core/service/core-models';
import _ from 'lodash';

@Injectable({providedIn: 'root'})
export class ProjectsService extends BaseService {
  apollo: Apollo;
  _http: any;
  header: any;
  loggedInUser: string;
  id: any;
  envDat = {
    siteId: sessionStorage.getItem('SITEID'),
    customerId: sessionStorage.getItem('CUSTOMERID'),
  };
  constructor(public http: HttpClient, private apolloProvider: Apollo, public coreService: CoreService) {
    super(http);
    this.apollo = this.apolloProvider;
    this.loggedInUser = coreService.getCurrentUsersLogInCookie();
    const projectHeaders: any = {
      'x-api-key': sessionStorage.getItem('OCCUPANCY_SVC_KEY'),
      'x-site-id': sessionStorage.getItem('SITEID'),
      'x-customer-id': sessionStorage.getItem('CUSTOMERID'),
      // 'x-token': this.header.headers['x-token'],
    };
    this.apollo.create(
      {cache: new InMemoryCache(), uri: sessionStorage.getItem('OCCUPANCY_SVC_GRAPHQL_URI'), headers: {...projectHeaders}},
      'projects'
    );
  }

  getProject<T>(Id: string): Observable<ProjectModel[]> {
    const data = [];
    return this.apollo
      .use('projects')
      .query({
        query: this.queryProject(),
        variables: {
          Id,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response: any) => {
          for (const edge of response.data.projects.edges) {
            data.push(edge.node);
          }
          return data;
        })
      );
  }

  queryProject() {
    return gql`
      query Projects($Id: String) {
        projects {
          edges {
            node {
              id
              customerId
              siteId
              name
              projectNumber
              projectCode
              ampProject
              dateOfFullAvailability
              projectSegmentCode
              createdOn
              createdBy
              modifiedOn
              modifiedBy
              tracsMailboxNumber
              programId
              customAttributes {
                id
                textValue
                booleanValue
                dateTimeValue
                entityInstanceId
                entityInstanceIdAsGuid
                entityAttributeId
                siteId
              }
              taxCreditSetAsides {
                projectId
                siteId
                unitsAt30
                unitsAt40
                unitsAt50
                unitsAt60
                unitsAtOther
              }
              projectOverrides {
                id
                projectId
                calcKey
                assistanceCalcKey
                elderlyDeductionAge
                allowableDependentDeduction
                totalTenantPaymentPercentage
                nearElderAge
                minTTP
                observeNahasda30Percent
                incomeBased
                monthOfFiscalYearEnd
                fundingSource
                imputedAssetRate
                allowableElderlyDisabilityDeduction
                paymentStandardId
                incomeLimitAreaId
                taxCreditIncomeLimitAreaId
                rentLimitAreaId
                siteId
                projectFeeTable {
                  id
                  projectOverridesId
                  feeTableType
                  effectiveDate
                  feeTableValues {
                    id
                    feeTableId
                    householdSize
                    amount
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  getProjects<T>(): Observable<ProjectModel[]> {
    const data = [];
    return this.apollo
      .use('projects')
      .query({
        query: this.queryProjects(),
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response: any) => {
          for (const edge of response.data.projects.edges) {
            edge.node._id = edge.node.id;
            edge.node.rentCalc = edge.node.projectOverrides;
            data.push(edge.node);
          }
          return data;
        })
      );
  }

  queryProjects() {
    return gql`
      query Projects {
        projects(first: 50) {
          edges {
            node {
              id
              customerId
              siteId
              name
              projectNumber
              projectCode
              ampProject
              dateOfFullAvailability
              projectSegmentCode
              createdOn
              createdBy
              modifiedOn
              modifiedBy
              tracsMailboxNumber
              programId
              customAttributes {
                id
                textValue
                booleanValue
                dateTimeValue
                entityInstanceId
                entityInstanceIdAsGuid
                entityAttributeId
                siteId
              }
              taxCreditSetAsides {
                projectId
                siteId
                unitsAt30
                unitsAt40
                unitsAt50
                unitsAt60
                unitsAtOther
              }
              projectOverrides {
                id
                projectId
                calcKey
                assistanceCalcKey
                elderlyDeductionAge
                allowableDependentDeduction
                totalTenantPaymentPercentage
                nearElderAge
                minTTP
                observeNahasda30Percent
                incomeBased
                monthOfFiscalYearEnd
                fundingSource
                imputedAssetRate
                allowableElderlyDisabilityDeduction
                paymentStandardId
                incomeLimitAreaId
                taxCreditIncomeLimitAreaId
                projectFeeTable {
                  id
                  projectOverridesId
                  feeTableType
                  effectiveDate
                  feeTableValues {
                    id
                    feeTableId
                    householdSize
                    amount
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  createProject<T>(project: CreateProjectCommand): Observable<FetchResult<T>> {
    const command: CreateProjectCommand = {
      customerId: project.customerId,
      siteId: project.siteId,
      name: project.name,
      projectCode: project.projectCode,
      projectNumber: project.projectNumber,
      programId: project.programId,
      createdBy: project.createdBy,
    };
    return this.apollo.use('projects').mutate({
      mutation: gql`
        mutation CreateProject($command: CreateProjectCommandInput!) {
          createProject(command: $command) {
            commandName
            status
            issuedOn
            acceptedOn
            succeededOn
            failedOn
            failureReason
            affectedEntity {
              id
              customerId
              siteId
              name
              projectNumber
              projectCode
              ampProject
              dateOfFullAvailability
              projectSegmentCode
              createdOn
              createdBy
              modifiedOn
              modifiedBy
              tracsMailboxNumber
              programId
              taxCreditSetAsides {
                projectId
                siteId
                unitsAt30
                unitsAt40
                unitsAt50
                unitsAt60
                unitsAtOther
              }
              projectOverrides {
                id
                projectId
                calcKey
                assistanceCalcKey
                elderlyDeductionAge
                allowableDependentDeduction
                totalTenantPaymentPercentage
                nearElderAge
                minTTP
                observeNahasda30Percent
                incomeBased
                monthOfFiscalYearEnd
                fundingSource
                imputedAssetRate
                allowableElderlyDisabilityDeduction
                paymentStandardId
                incomeLimitAreaId
                taxCreditIncomeLimitAreaId
                projectFeeTable {
                  id
                  projectOverridesId
                  feeTableType
                  effectiveDate
                  feeTableValues {
                    id
                    feeTableId
                    householdSize
                    amount
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        command,
      },
    });
  }
  updateProject<T>(project: UpdateProjectCommand): Observable<FetchResult<T>> {
    const command: UpdateProjectCommand = {
      customerId: project.customerId,
      siteId: project.siteId,
      id: project.id,
      name: project.name,
      projectCode: project.projectCode,
      projectNumber: project.projectNumber,
      modifiedBy: project.modifiedBy,
    };
    return this.apollo.use('projects').mutate({
      mutation: gql`
        mutation UpdateProject($command: UpdateProjectCommandInput!) {
          updateProject(command: $command) {
            commandName
            status
            issuedOn
            acceptedOn
            succeededOn
            failedOn
            failureReason
            affectedEntity {
              id
              customerId
              siteId
              name
              projectNumber
              projectCode
              ampProject
              dateOfFullAvailability
              projectSegmentCode
              createdOn
              createdBy
              modifiedOn
              modifiedBy
              tracsMailboxNumber
              programId
              taxCreditSetAsides {
                projectId
                siteId
                unitsAt30
                unitsAt40
                unitsAt50
                unitsAt60
                unitsAtOther
              }
              projectOverrides {
                id
                projectId
                calcKey
                assistanceCalcKey
                elderlyDeductionAge
                allowableDependentDeduction
                totalTenantPaymentPercentage
                nearElderAge
                minTTP
                observeNahasda30Percent
                incomeBased
                monthOfFiscalYearEnd
                fundingSource
                imputedAssetRate
                allowableElderlyDisabilityDeduction
                paymentStandardId
                incomeLimitAreaId
                taxCreditIncomeLimitAreaId
                projectFeeTable {
                  id
                  projectOverridesId
                  feeTableType
                  effectiveDate
                  feeTableValues {
                    id
                    feeTableId
                    householdSize
                    amount
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        command,
      },
    });
  }

  createProjectOverride<T>(command: CreateProjectOverrideCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('programs').mutate({
        mutation: gql`
          mutation CreateProjectOverrides($command: CreateProjectOverridesCommandInput!) {
            createProjectOverrides(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
              affectedEntity {
                id
                projectId
                calcKey
                assistanceCalcKey
                elderlyDeductionAge
                allowableDependentDeduction
                totalTenantPaymentPercentage
                nearElderAge
                observeNahasda30Percent
                incomeLimitAreaId
                taxCreditIncomeLimitAreaId
                rentLimitAreaId
                imputedAssetRate
                siteId
              }
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  updateProjectOverride<T>(command: UpdateProjectOverrideCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('programs').mutate({
        mutation: gql`
          mutation UpdateProjectOverrides($command: UpdateProjectOverridesCommandInput!) {
            updateProjectOverrides(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
              affectedEntity {
                id
                projectId
                calcKey
                assistanceCalcKey
                elderlyDeductionAge
                allowableDependentDeduction
                totalTenantPaymentPercentage
                nearElderAge
                observeNahasda30Percent
                incomeLimitAreaId
                taxCreditIncomeLimitAreaId
                rentLimitAreaId
                imputedAssetRate
                siteId
              }
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  deleteProjectOverride<T>(command: DeleteProjectOverrideCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('programs').mutate({
        mutation: gql`
          mutation DeleteProjectOverrides($command: DeleteProjectOverridesCommandInput!) {
            deleteProjectOverrides(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  createProjectFeeTable<T>(command: CreateProjectFeeTableCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('projects').mutate({
        mutation: gql`
          mutation createProjectFeeTable($command: CreateProjectFeeTableCommandInput!) {
            createProjectFeeTable(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  updateProjectFeeTable<T>(command: UpdateProjectFeeTableCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('projects').mutate({
        mutation: gql`
          mutation updateProjectFeeTable($command: UpdateProjectFeeTableCommandInput!) {
            updateProjectFeeTable(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  deleteProjectFeeTable<T>(command: DeleteProjectFeeTableCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('projects').mutate({
        mutation: gql`
          mutation deleteProjectFeeTable($command: DeleteProjectFeeTableCommandInput!) {
            deleteProjectFeeTable(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  getSetAsides<T>(projectId: string): Observable<ProjectModel[]> {
    const data = [];
    return this.apollo
      .use('projects')
      .query({
        query: this.querySetAsides(),
        variables: {
          projectId,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response: any) => {
          for (const edge of response.data.taxCreditSetAsides.edges) {
            data.push(edge.node);
          }
          return data;
        })
      );
  }

  querySetAsides() {
    return gql`
      query TaxCreditSetAsides {
        taxCreditSetAsides {
          nodes {
            unitsAt30
            unitsAt40
            unitsAt50
            unitsAt60
            unitsAtOther
            siteId
            projectId
          }
        }
      }
    `;
  }

  createTaxCreditSetAsides<T>(command: CreateTaxCreditSetAsidesCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('projects').mutate({
        mutation: gql`
          mutation CreateTaxCreditSetAsides($command: CreateTaxCreditSetAsidesCommandInput!) {
            createTaxCreditSetAsides(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
              affectedEntity {
                unitsAt30
                unitsAt40
                unitsAt50
                unitsAt60
                unitsAtOther
                siteId
                projectId
              }
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  updateTaxCreditSetAsides<T>(command: UpdateTaxCreditSetAsidesCommand): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('projects').mutate({
        mutation: gql`
          mutation UpdateTaxCreditSetAsides($command: UpdateTaxCreditSetAsidesCommandInput!) {
            updateTaxCreditSetAsides(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
              affectedEntity {
                unitsAt30
                unitsAt40
                unitsAt50
                unitsAt60
                unitsAtOther
                siteId
                projectId
              }
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }

  async saveProject(projectToSave: ProjectModel, projectExisting?: ProjectModel): Promise<ComplexSaveResult> {
    const response: ComplexSaveResult = {
      success: true,
      errorMessage: '',
      affectedEntity: projectExisting ?? null,
    };
    let attributeResponse: ComplexSaveResult = _.clone(response);
    try {
      if (projectExisting && projectExisting.id) {
        if (projectToSave.taxCreditSetAsides) {
          const taxCreditDirty = !this.coreService.areEqual(projectToSave.taxCreditSetAsides, projectExisting.taxCreditSetAsides);
          if (taxCreditDirty) {
            const taxCreditToSave = projectToSave.taxCreditSetAsides as TaxCreditSetAsides;
            const taxCreditExisting = projectExisting.taxCreditSetAsides as TaxCreditSetAsides;
            await this.saveTaxCreditSetAsides(taxCreditToSave, taxCreditExisting, projectExisting.id);
          }
        }
        const projectDirty = !this.coreService.areEqual(projectToSave, projectExisting);
        if (projectDirty) {
          const command = this.buildUpdateProjectMutation(projectToSave, projectExisting);
          const result = (await this.updateProject(command).toPromise()) as {data: IUpdateProjectDataResponse};
          if (result.data.updateProject.status !== 'SUCCESS') {
            response.success = false;
            response.errorMessage = `${result.data.updateProject.failureReason || 'Unknown error'}`;
            return response;
          } else {
            response.affectedEntity = result.data.updateProject.affectedEntity;
          }
        }
        if (projectToSave.rentCalc) {
          const projectRentcalcDirty = !this.coreService.areEqual(projectToSave.rentCalc, projectExisting.rentCalc);
          if (projectRentcalcDirty) {
            const rentCalcToSave = projectToSave.rentCalc as ProjectOverride;
            const rentCalcExisting = projectExisting.rentCalc as ProjectOverride;
            await this.saveProjectRentCalc(rentCalcToSave, rentCalcExisting, projectExisting.id);
          }
        } else {
          //!unitToSave.overrideRentCalc && unitExisting.overrideRentCalc
          if (!projectToSave.rentCalc && projectExisting.projectOverrides) {
            const command = this.buildDeleteProjectOverrideMutation(projectExisting.id);
            const result = (await this.deleteProjectOverride(command).toPromise()) as {data: IDeleteProjectOverrideDataResponse};
            if (result.data.deleteProjectOverrides.status !== 'SUCCESS') {
              response.success = false;
              response.errorMessage = `${result.data.deleteProjectOverrides.failureReason || 'Unknown error'}`;
              return response;
            } else {
              response.affectedEntity = result.data.deleteProjectOverrides.affectedEntity;
            }
          }
          projectToSave.customAttributes = projectToSave.customAttributes.sort((a, b) =>
            a.entityAttributeId.localeCompare(b.entityAttributeId)
          );

          projectExisting.customAttributes = projectExisting.customAttributes.sort((a, b) =>
            a.entityAttributeId.localeCompare(b.entityAttributeId)
          );
          const attributesDirty = !this.coreService.areEqual(projectToSave.customAttributes, projectExisting.customAttributes, true);
          if (attributesDirty) {
            attributeResponse = await this.saveCustomAttributes(projectToSave, projectExisting);
            if (!attributeResponse.success) {
              return attributeResponse;
            }
          }
        }
      } else {
        const command = this.buildCreateProjectMutation(projectToSave);
        const result = (await this.createProject(command).toPromise()) as {data: ICreateProjectDataResponse};
        if (result.data.createProject.status !== 'SUCCESS') {
          response.success = false;
          response.errorMessage = `${result.data.createProject.failureReason || 'Unknown error'}`;
          return response;
        } else {
          response.affectedEntity = result.data.createProject.affectedEntity;
          if (projectToSave.rentCalc) {
            const rentCalcToSave = projectToSave.rentCalc as ProjectOverride;
            await this.saveProjectRentCalc(rentCalcToSave, null, response.affectedEntity.id);
          }

          if (projectToSave.customAttributes.length > 0) {
            projectToSave.id = response.affectedEntity.id;
            attributeResponse = await this.saveCustomAttributes(projectToSave);
            if (!attributeResponse.success) {
              return attributeResponse;
            }
          }
          if (projectToSave.taxCreditSetAsides) {
            const taxCreditToSave = projectToSave.taxCreditSetAsides as TaxCreditSetAsides;
            await this.saveTaxCreditSetAsides(taxCreditToSave, null, result.data.createProject.affectedEntity.id);
          }
        }
      }
    } catch (error) {
      console.error('Error in saveProject:', error);
      response.errorMessage = 'Error occurred during save operation';
    }
    return response;
  }
  private buildUpdateProjectMutation(projectToSave: ProjectModel, projectExisting: ProjectModel): UpdateProjectCommand {
    const projectToSaveKeys = Object.keys(projectToSave);
    const currentuser: string = this.coreService.getCurrentUsersLogInCookie();
    const command: UpdateProjectCommand = {
      customerId: this.customerId,
      siteId: this.siteId,
      id: projectToSave.id,
      name: projectToSave.name,
      projectCode: projectToSave.projectCode,
      projectNumber: projectToSave.projectNumber,
      modifiedBy: currentuser,
    };
    for (const key of projectToSaveKeys) {
      const isObject = this.coreService.isObject(projectToSave[key]) || this.coreService.isObject(projectExisting[key]);
      const isArray = Array.isArray(projectToSave[key]);
      if (isArray || isObject) {
        continue;
      }
      if (!Object.prototype.hasOwnProperty.call(projectExisting, key) || projectToSave[key] !== projectExisting[key]) {
        const commandKey = key;
        command[commandKey] = projectToSave[key];
      }
    }
    return command;
  }
  private buildCreateProjectMutation(projectToSave: ProjectModel): CreateProjectCommand {
    const projectToSaveKeys = Object.keys(projectToSave);
    const currentuser: string = this.coreService.getCurrentUsersLogInCookie();
    const command: CreateProjectCommand = {
      customerId: this.customerId,
      siteId: this.siteId,
      name: projectToSave.name,
      projectCode: projectToSave.projectCode,
      projectNumber: projectToSave.projectNumber,
      programId: projectToSave.program,
      createdBy: currentuser,
    };
    for (const key of projectToSaveKeys) {
      const isObject = this.coreService.isObject(projectToSave[key]);
      const isArray = Array.isArray(projectToSave[key]);
      if (projectToSave[key] && !isObject && !isArray) {
        command[key] = projectToSave[key];
      }
    }
    return command;
  }

  async saveTaxCreditSetAsides(taxCreditToSave: TaxCreditSetAsides, taxCreditExisting?: TaxCreditSetAsides, projectId?: string) {
    const response: ComplexSaveResult = {
      success: true,
      errorMessage: '',
      affectedEntity: taxCreditExisting ?? null,
    };
    try {
      if (taxCreditExisting && taxCreditExisting.projectId) {
        const taxCreditDirty = !this.coreService.areEqual(taxCreditToSave, taxCreditExisting);
        if (taxCreditDirty) {
          const command = this.buildUpdateTaxCreditSetAsidesMutation(taxCreditToSave);
          const result = (await this.updateTaxCreditSetAsides(command).toPromise()) as {data: IUpdateTaxCreditSetAsidesDataResponse};
          if (result.data.updateTaxCreditSetAsides.status !== 'SUCCESS') {
            response.success = false;
            response.errorMessage = `${result.data.updateTaxCreditSetAsides.failureReason || 'Unknown error'}`;
            return response;
          } else {
            response.affectedEntity = result.data.updateTaxCreditSetAsides.affectedentity;
          }
        }
      } else {
        const command = this.buildCreateTaxCreditSetAsidesMutation(taxCreditToSave, projectId);
        const result = (await this.createTaxCreditSetAsides(command).toPromise()) as {data: ICreateTaxCreditSetAsidesDataResponse};
        if (result.data.createTaxCreditSetAsides.status !== 'SUCCESS') {
          response.success = false;
          response.errorMessage = `${result.data.createTaxCreditSetAsides.failureReason || 'Unknown error'}`;
          return response;
        } else {
          response.affectedEntity = result.data.createTaxCreditSetAsides.affectedentity;
        }
      }
    } catch (error) {
      console.error('Error in saveTaxCreditSetAsides:', error);
      response.errorMessage = 'Error occurred during save operation';
    }
  }

  async saveProjectRentCalc(rentCalcToSave: ProjectOverride, rentCalcExisting?: ProjectOverride, projectId?: string) {
    const response: ComplexSaveResult = {
      success: true,
      errorMessage: '',
      affectedEntity: rentCalcExisting ?? null,
    };
    try {
      if (rentCalcToSave.imputedAssetRate && typeof rentCalcToSave.imputedAssetRate == 'string') {
        rentCalcToSave.imputedAssetRate = parseFloat(rentCalcToSave.imputedAssetRate);
      }
      if (rentCalcToSave.minTTP && typeof rentCalcToSave.minTTP == 'string') {
        rentCalcToSave.minTTP = parseFloat(rentCalcToSave.minTTP);
      }
      if (rentCalcExisting && rentCalcExisting.id) {
        const rentCalcDirty = !this.coreService.areEqual(rentCalcToSave, rentCalcExisting);
        if (rentCalcDirty) {
          const command = this.buildUpdateProjectOverrideMutation(rentCalcToSave);
          const result = (await this.updateProjectOverride(command).toPromise()) as {data: IUpdateProjectOverrideDataResponse};
          if (result.data.updateProjectOverrides.status !== 'SUCCESS') {
            response.success = false;
            response.errorMessage = `${result.data.updateProjectOverrides.failureReason || 'Unknown error'}`;
            return response;
          } else {
            response.affectedEntity = result.data.updateProjectOverrides.affectedEntity;
          }
        }
        if (rentCalcToSave.projectFeeTable.length > 0) {
          await this.saveProjectFeeTableAndValues(
            rentCalcToSave.projectFeeTable,
            rentCalcExisting.projectFeeTable,
            rentCalcExisting.id,
            rentCalcExisting.projectId
          );
        }
      } else {
        const command = this.buildCreateProjectOverridesMutation(rentCalcToSave, projectId);
        const result = (await this.createProjectOverride(command).toPromise()) as {data: ICreateProjectOverrideDataResponse};
        if (result.data.createProjectOverrides.status !== 'SUCCESS') {
          response.success = false;
          response.errorMessage = `${result.data.createProjectOverrides.failureReason || 'Unknown error'}`;
          return response;
        } else {
          response.affectedEntity = result.data.createProjectOverrides.affectedEntity;
          if (rentCalcToSave.projectFeeTable.length > 0) {
            await this.saveProjectFeeTableAndValues(
              rentCalcToSave.projectFeeTable,
              null,
              result.data.createProjectOverrides.affectedEntity.id,
              result.data.createProjectOverrides.affectedEntity.projectId
            );
          }
        }
      }
    } catch (error) {
      console.error('Error in saveProjectOverride:', error);
      response.errorMessage = 'Error occurred during save operation';
    }
  }
  private buildCreateProjectOverridesMutation(projectOverrideToSave: ProjectOverride, projectId: string) {
    const command: CreateProjectOverrideCommand = {
      createdBy: this.loggedInUser,
      customerId: this.customerId,
      siteId: this.siteId,
      projectId: projectId,
      calcKey: projectOverrideToSave.calcKey,
      assistanceCalcKey: projectOverrideToSave.assistanceCalcKey,
      elderlyDeductionAge: projectOverrideToSave.elderlyDeductionAge,
      totalTenantPaymentPercentage: projectOverrideToSave.totalTenantPaymentPercentage,
      allowableDependentDeduction: projectOverrideToSave.allowableDependentDeduction * 100,
      allowableElderlyDisabilityDeduction: projectOverrideToSave.allowableElderlyDisabilityDeduction * 100,
      nearElderAge: projectOverrideToSave.nearElderAge,
      observeNahasda30Percent: projectOverrideToSave.observeNahasda30Percent,
      minTTP: projectOverrideToSave.minTTP,
      incomeLimitAreaId: projectOverrideToSave.incomeLimitAreaId,
      taxCreditIncomeLimitAreaId: projectOverrideToSave.taxCreditIncomeLimitAreaId,
      rentLimitAreaId: projectOverrideToSave.rentLimitAreaId,
      paymentStandardId: projectOverrideToSave.paymentStandardId,
      monthOfFiscalYearEnd: projectOverrideToSave.monthOfFiscalYearEnd,
      fundingSource: projectOverrideToSave.fundingSource,
      incomeBased: false,
    };
    return command;
  }

  private buildUpdateProjectOverrideMutation(projectOverrideToSave: ProjectOverride) {
    const command: UpdateProjectOverrideCommand = {
      id: projectOverrideToSave.id,
      modifiedBy: this.loggedInUser,
      customerId: this.customerId,
      siteId: this.siteId,
      calcKey: projectOverrideToSave.calcKey,
      assistanceCalcKey: projectOverrideToSave.assistanceCalcKey,
      elderlyDeductionAge: projectOverrideToSave.elderlyDeductionAge,
      totalTenantPaymentPercentage: projectOverrideToSave.totalTenantPaymentPercentage,
      allowableDependentDeduction: projectOverrideToSave.allowableDependentDeduction * 100,
      allowableElderlyDisabilityDeduction: projectOverrideToSave.allowableElderlyDisabilityDeduction * 100,
      nearElderAge: projectOverrideToSave.nearElderAge,
      observeNahasda30Percent: projectOverrideToSave.observeNahasda30Percent,
      minTTP: projectOverrideToSave.minTTP,
      incomeLimitAreaId: projectOverrideToSave.incomeLimitAreaId,
      taxCreditIncomeLimitAreaId: projectOverrideToSave.taxCreditIncomeLimitAreaId,
      rentLimitAreaId: projectOverrideToSave.rentLimitAreaId,
      paymentStandardId: projectOverrideToSave.paymentStandardId,
      monthOfFiscalYearEnd: projectOverrideToSave.monthOfFiscalYearEnd,
      fundingSource: projectOverrideToSave.fundingSource,
      incomeBased: false,
    };
    return command;
  }

  private buildCreateTaxCreditSetAsidesMutation(taxCreditToSave: TaxCreditSetAsides, projectId: string) {
    const command: CreateTaxCreditSetAsidesCommand = {
      customerId: this.customerId,
      projectId: projectId,
      siteId: this.siteId,
      unitsAt30: taxCreditToSave.unitsAt30,
      unitsAt40: taxCreditToSave.unitsAt40,
      unitsAt50: taxCreditToSave.unitsAt50,
      unitsAt60: taxCreditToSave.unitsAt60,
      unitsAtOther: taxCreditToSave.unitsAtOther,
    };
    return command;
  }

  private buildUpdateTaxCreditSetAsidesMutation(taxCreditToSave: TaxCreditSetAsides) {
    const command: UpdateTaxCreditSetAsidesCommand = {
      customerId: this.customerId,
      projectId: taxCreditToSave.projectId,
      siteId: this.siteId,
      unitsAt30: taxCreditToSave.unitsAt30,
      unitsAt40: taxCreditToSave.unitsAt40,
      unitsAt50: taxCreditToSave.unitsAt50,
      unitsAt60: taxCreditToSave.unitsAt60,
      unitsAtOther: taxCreditToSave.unitsAtOther,
    };
    return command;
  }

  private buildDeleteProjectOverrideMutation(id: string) {
    const command: DeleteProjectOverrideCommand = {
      id: id,
      customerId: this.customerId,
      siteId: this.siteId,
    };
    return command;
  }

  async saveProjectFeeTableAndValues(
    projectFeeTablesToSave: ProjectFeeTable[],
    projectFeeTableExisting: ProjectFeeTable[],
    rentcalcId: string,
    projectId: string
  ) {
    const createCommandList: CreateProjectFeeTableCommand[] = [];
    const updateCommandList: UpdateProjectFeeTableCommand[] = [];
    const deleteCommandList: DeleteProjectFeeTableCommand[] = [];
    projectFeeTablesToSave.forEach((pft, index) => {
      switch (true) {
        case pft.status == ProjectFeeStatus.Create:
        case pft.status == ProjectFeeStatus.CreateAndModified: {
          const createCommand = this.buildCreateProjectFeeTableCommand(pft, rentcalcId, projectId);
          createCommandList.push(createCommand);
          break;
        }

        case pft.status == ProjectFeeStatus.Modified: {
          const feeTableDirty = !this.coreService.areEqual(pft, projectFeeTableExisting[index], true);
          if (feeTableDirty) {
            const updateCommand = this.buildUpdateProjectFeeTableCommand(pft, projectId);
            updateCommandList.push(updateCommand);
          }
          break;
        }

        case pft.status == ProjectFeeStatus.Delete: {
          const deleteCommand = this.buildDeleteProjectFeeTableCommand(pft.id, projectId);
          deleteCommandList.push(deleteCommand);
          break;
        }
      }
    });
    try {
      const resultArray = [];
      if (createCommandList.length > 0) {
        for (const command of createCommandList) {
          const result = await this.createProjectFeeTable(command).toPromise();
          resultArray.push(result);
        }
      }

      if (updateCommandList.length > 0) {
        for (const command of updateCommandList) {
          const result = await this.updateProjectFeeTable(command).toPromise();
          resultArray.push(result);
        }
      }

      if (deleteCommandList.length > 0) {
        for (const command of deleteCommandList) {
          const result = await this.deleteProjectFeeTable(command).toPromise();
          resultArray.push(result);
        }
      }
      return resultArray;
    } catch (error) {
      console.error('Error in saveProjectFeeTable:', error);
    }
  }

  private buildCreateProjectFeeTableCommand(projectFeeTable: ProjectFeeTable, rentCalcId: string, projectId: string) {
    const command: CreateProjectFeeTableCommand = {
      projectOverridesId: rentCalcId,
      feeTableType: projectFeeTable.feeTableType,
      effectiveDate: projectFeeTable.effectiveDate,
      project: projectId,
      createdBy: this.loggedInUser,
      feeTableValues: projectFeeTable.feeTableValues.map(ftv => {
        if (ftv.amount) {
          return ftv.amount;
        } else {
          return 0;
        }
      }),
      siteId: this.siteId,
      customerId: this.customerId,
    };
    return command;
  }

  private buildUpdateProjectFeeTableCommand(projectFeeTable: ProjectFeeTable, projectId: string) {
    const command: UpdateProjectFeeTableCommand = {
      id: projectFeeTable.id,
      effectiveDate: projectFeeTable.effectiveDate,
      project: projectId,
      modifiedBy: this.loggedInUser,
      feeTableValues: projectFeeTable.feeTableValues.map(ftv => {
        if (ftv.amount) {
          return ftv.amount;
        } else {
          return 0;
        }
      }),
      siteId: this.siteId,
      customerId: this.customerId,
    };
    return command;
  }

  private buildDeleteProjectFeeTableCommand(projectFeeTableId: string, projectId: string) {
    const command: DeleteProjectFeeTableCommand = {
      id: projectFeeTableId,
      project: projectId,
      deletedBy: this.loggedInUser,
      siteId: this.siteId,
      customerId: this.customerId,
    };
    return command;
  }

  createProjectCustomAttribute<T>(command: ICustomAttributesValue): Observable<FetchResult<T>> {
    try {
      return this.apollo.use('projects').mutate({
        mutation: gql`
          mutation CreateProjectAttributeValue($command: CreateProjectAttributeValueCommandInput!) {
            createProjectAttributeValue(command: $command) {
              commandName
              status
              issuedOn
              acceptedOn
              succeededOn
              failedOn
              failureReason
              affectedEntity {
                textValue
                booleanValue
                dateTimeValue
                siteId
              }
            }
          }
        `,
        variables: {
          command,
        },
      });
    } catch (error) {
      this.coreService.displayError(error);
      console.log(error);
    }
  }
  updateProjectCustomAttribute<T>(command: UpdateProjectCustomAttributes): Observable<FetchResult<T>> {
    return this.apollo.use('projects').mutate({
      mutation: gql`
        mutation UpdateProjectAttributeValue($command: UpdateProjectAttributeValueCommandInput!) {
          updateProjectAttributeValue(valueCommand: $command) {
            commandName
            status
            issuedOn
            acceptedOn
            succeededOn
            failedOn
            failureReason
            affectedEntity {
              id
              textValue
              booleanValue
              dateTimeValue
            }
          }
        }
      `,
      variables: {
        command,
      },
    });
  }

  async saveCustomAttributes(projectToSave: ProjectModel, projectExisting?: ProjectModel): Promise<ComplexSaveResult> {
    const response: ComplexSaveResult = {
      success: true,
      errorMessage: '',
      affectedEntity: projectToSave.customAttributes ?? null,
    };
    for (const attribute of projectToSave.customAttributes) {
      const customAttributeValue: ICustomAttributesValue = attribute as CustomAttributesValue;
      const attributeCommand: IProjectAttributeCommand = {
        ...this.envDat,
        textValue: customAttributeValue.textValue,
        booleanValue: customAttributeValue.booleanValue,
        dateTimeValue: customAttributeValue.dateTimeValue,
      };
      switch (true) {
        case !customAttributeValue.id && customAttributeValue.attributeType == 'toggle': {
          const createAttributeCommand: ICustomAttributesValue = {
            ...attributeCommand,

            entityAttributeId: customAttributeValue.entityAttributeId,
            entityInstanceId: projectToSave.id,
          };
          createAttributeCommand.booleanValue = customAttributeValue.booleanValue ? customAttributeValue.booleanValue : false;
          const result = await this.createProjectCustomAttribute(createAttributeCommand).toPromise();
          if ((result.data as ICreateProjectAttributeDataResponse).createProjectAttributeValue.status !== 'SUCCESS') {
            if (!response.success) {
              response.errorMessage += `${
                (result.data as ICreateProjectAttributeDataResponse).createProjectAttributeValue.failureReason
              }\n`;
            }
          }
          break;
        }
        case !customAttributeValue.id &&
          customAttributeValue.attributeType !== 'toggle' &&
          (customAttributeValue.dateTimeValue !== null || customAttributeValue.textValue !== null): {
          //new attribute w/ value
          const createAttributeCommand: ICustomAttributesValue = {
            ...attributeCommand,
            entityAttributeId: customAttributeValue.entityAttributeId,
            entityInstanceId: projectToSave.id,
          };
          const result = await this.createProjectCustomAttribute(createAttributeCommand).toPromise();
          if ((result.data as ICreateProjectAttributeDataResponse).createProjectAttributeValue.status !== 'SUCCESS') {
            if (!response.success) {
              response.errorMessage += `${
                (result.data as ICreateProjectAttributeDataResponse).createProjectAttributeValue.failureReason
              }\n`;
            }
          }
          break;
        }
        case !!attribute.id: {
          //existing attribute check if dirty
          const existingAttribute = (projectExisting.customAttributes as ICustomAttributesValue[]).find(a => a.id == attribute.id);
          const attributeValueFortest = new CustomAttributesValue(existingAttribute as CustomAttributesValue);
          if (!this.coreService.areEqual(attributeValueFortest, attribute)) {
            if (customAttributeValue.attributeType == 'toggle') {
              const updateAttributeCommand: UpdateProjectCustomAttributes = {
                textValue: customAttributeValue.textValue,
                booleanValue: customAttributeValue.booleanValue = customAttributeValue.booleanValue
                  ? customAttributeValue.booleanValue
                  : false,
                dateTimeValue: customAttributeValue.dateTimeValue,
                id: attribute.id,
                siteId: sessionStorage.getItem('SITEID'),
                customerId: sessionStorage.getItem('CUSTOMERID'),
              };
              const result = await this.updateProjectCustomAttribute(updateAttributeCommand).toPromise();
              if ((result.data as IUpdateProjectAttributeDataResponse).updateProjectAttributeValue.status !== 'SUCCESS') {
                if (!response.success) {
                  response.errorMessage += `${
                    (result.data as IUpdateProjectAttributeDataResponse).updateProjectAttributeValue.failureReason
                  }\n`;
                }
              }
            } else {
              // update attribute
              const updateAttributeCommand: UpdateProjectCustomAttributes = {
                textValue: customAttributeValue.textValue,
                booleanValue: customAttributeValue.booleanValue,
                dateTimeValue: customAttributeValue.dateTimeValue,
                id: attribute.id,
                siteId: sessionStorage.getItem('SITEID'),
                customerId: sessionStorage.getItem('CUSTOMERID'),
              };
              const result = await this.updateProjectCustomAttribute(updateAttributeCommand).toPromise();
              if ((result.data as IUpdateProjectAttributeDataResponse).updateProjectAttributeValue.status !== 'SUCCESS') {
                if (!response.success) {
                  response.errorMessage += `${
                    (result.data as IUpdateProjectAttributeDataResponse).updateProjectAttributeValue.failureReason
                  }\n`;
                }
              }
            }
          }
          break;
        }
      }
    }
    return response;
  }
}
